// The content floor: the narrowest a data column may render without clipping content that cannot bend. Text
// that wraps or truncates bends, so it never raises a floor. Content that cannot bend does: a button, a split
// button or a badge (each nowrap in its own stylesheet), and every value in a cell that keeps to one line
// without truncating (a nowrap text, numeric or date cell, a summary total). The floor is that content's
// width plus the cell's horizontal padding, and an interactive control's padding holds its focus ring on
// both sides (RING_SPACE, the same rule controlFloor applies to control columns).
//
// The floor is measured in the browser from the rendered body and summary cells, so it follows the real
// fonts, labels and density. The header adds its longest label word (headerExtent below). Without layout
// (jsdom) nothing measures and a column keeps its configured minimum. view.js applies the floor:
// effectiveMin, clampWidth and renderedWidth.

import { useLayoutEffect, useState } from 'react'

import { RING_SPACE } from './controlSize'

/** Content that keeps its natural width at any column width. */
export const NON_BENDING = '.me-btn-split, .me-btn, .me-badge-tint'

/** Non-bending content that takes focus and draws Buttons' ring outside its box. */
export const INTERACTIVE = '.me-btn-split, .me-btn'

/** A value that truncates; it discloses its full value instead of raising the floor. */
export const CLIPPED = '.me-table-clipped'

// the data cells a floor reads: body rows and the summary row; the header is read on its own (headerExtent)
const CELLS =
  ':scope > tbody > tr > td[data-column-key], :scope > tfoot > tr > [data-column-key]'

const ONE_LINE = new Set(['nowrap', 'pre'])

const px = value => {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

const rectWidth = node => {
  const rect =
    node && node.getBoundingClientRect && node.getBoundingClientRect()
  return rect && Number.isFinite(rect.width) ? rect.width : 0
}

// the outermost non-bending elements in a cell, outside any truncating value
const blocks = cell =>
  Array.from(cell.querySelectorAll(NON_BENDING)).filter(
    el =>
      !el.closest(CLIPPED) &&
      !(el.parentElement && el.parentElement.closest(NON_BENDING))
  )

// text a screen reader alone reads, and a value that truncates, take no width
const SKIP = `.sr-only, ${CLIPPED}`

// The extent of a one-line cell's content, read from its glyphs and its non-bending elements rather than
// from any box, since a block box is as wide as its cell whatever it holds.
const lineWidth = (cell, found) => {
  let left = Infinity
  let right = -Infinity
  const add = rect => {
    if (!rect || !(rect.width > 0)) return
    left = Math.min(left, rect.left)
    right = Math.max(right, rect.right)
  }
  found.forEach(el => add(el.getBoundingClientRect()))
  if (typeof document.createRange === 'function') {
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT)
    const range = document.createRange()
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const parent = node.parentElement
      if (!node.nodeValue.trim() || (parent && parent.closest(SKIP))) continue
      if (found.some(el => el.contains(node))) continue
      range.selectNodeContents(node)
      if (typeof range.getBoundingClientRect === 'function')
        add(range.getBoundingClientRect())
    }
  }
  return right > left ? right - left : 0
}

/**
 * One cell's floor in CSS pixels, or 0 when all of its content bends (or nothing measured).
 * @param {HTMLElement} cell a body or summary cell
 * @returns {number}
 */
export const cellFloor = cell => {
  const style = window.getComputedStyle(cell)
  // a truncating cell (wrap: 'ellipsis', a Compact summary total) never clips past its ellipsis
  if (style.textOverflow === 'ellipsis') return 0
  const found = blocks(cell)
  let content = 0
  if (ONE_LINE.has(style.whiteSpace) && !cell.querySelector(CLIPPED))
    content = lineWidth(cell, found)
  found.forEach(el => {
    content = Math.max(content, rectWidth(el), el.scrollWidth || 0)
  })
  if (!(content > 0)) return 0
  const ring = found.some(el => el.matches(INTERACTIVE)) ? RING_SPACE : 0
  return Math.ceil(
    content +
      Math.max(px(style.paddingLeft), ring) +
      Math.max(px(style.paddingRight), ring)
  )
}

/**
 * Every data column's floor in a rendered table, keyed by column key; a column with no non-bending content
 * has no entry. Null when the table renders no data cells (loading, an empty state), so the caller keeps
 * the floors it had.
 * @param {?HTMLTableElement} table
 * @returns {?Object<string, number>}
 */
export const measureFloors = table => {
  if (!table) return null
  const cells = table.querySelectorAll(CELLS)
  if (!table.querySelector(':scope > tbody > tr > td[data-column-key]'))
    return null
  const floors = {}
  cells.forEach(cell => {
    const floor = cellFloor(cell)
    const key = cell.dataset.columnKey
    if (floor > 0 && !(floors[key] >= floor)) floors[key] = floor
  })
  return floors
}

// ---- the header's floor and every column's natural width ----
//
// A header label bends: it truncates. It may truncate a multi-word label after its longest word, never inside
// a word, so its longest single word counts toward the floor: the word at the header's font, plus what the
// cell holds beside the label (a sort control's padding, gap and glyph), plus the cell's padding, which for a
// sort control holds its focus ring. The same sum over the whole label is the header's natural width; a
// column narrower than its header's or a truncating value's natural width shows truncated text, and the
// colgroup gives such columns spare width first (spareWidths, view.js).

// the header cells a floor reads, and the label each one renders
const HEAD_CELLS = ':scope > thead > tr > th[data-column-key]'
const HEAD_LABEL = '.me-th-sort-label, .me-table-head-static'
const SORT_CONTROL = '.me-th-sort'

const WORD = /\S+/g

// a text range's rendered width, 0 without layout (jsdom has no Range geometry)
const rangeWidth = range =>
  typeof range.getBoundingClientRect === 'function'
    ? range.getBoundingClientRect().width || 0
    : 0

const textOf = el => {
  const out = []
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement
    if (node.nodeValue.trim() && !(parent && parent.closest('.sr-only')))
      out.push(node)
  }
  return out
}

/**
 * The rendered width of an element's text: all of it, and its longest single word. Both are glyph extents,
 * so a label that truncates (its overflow is clipped, not laid out again) measures at its full length.
 * @param {HTMLElement} el
 * @returns {{full: number, word: number}}
 */
export const textWidths = el => {
  if (typeof document.createRange !== 'function') return { full: 0, word: 0 }
  const range = document.createRange()
  let left = Infinity
  let right = -Infinity
  let word = 0
  textOf(el).forEach(node => {
    range.selectNodeContents(node)
    if (typeof range.getBoundingClientRect === 'function') {
      const rect = range.getBoundingClientRect()
      if (rect && rect.width > 0) {
        left = Math.min(left, rect.left)
        right = Math.max(right, rect.right)
      }
    }
    const text = node.nodeValue
    WORD.lastIndex = 0
    for (let match = WORD.exec(text); match; match = WORD.exec(text)) {
      range.setStart(node, match.index)
      range.setEnd(node, match.index + match[0].length)
      word = Math.max(word, rangeWidth(range))
    }
  })
  return { full: right > left ? right - left : 0, word }
}

/**
 * A header cell's floor (its longest word) and natural width (its whole label), in CSS pixels; both 0 for a
 * header with no visible label or without layout.
 * @param {HTMLElement} cell a header cell carrying data-column-key
 * @returns {{floor: number, natural: number}}
 */
export const headerExtent = cell => {
  const label = cell.querySelector(HEAD_LABEL)
  if (!label) return { floor: 0, natural: 0 }
  const { full, word } = textWidths(label)
  if (!(full > 0)) return { floor: 0, natural: 0 }
  const style = window.getComputedStyle(cell)
  const control = label.closest(SORT_CONTROL)
  // the sort control's own padding, gap and glyph: its box less its label's
  const beside = control
    ? Math.max(0, rectWidth(control) - rectWidth(label))
    : 0
  // a sort control, and a truncated static label (which takes focus to disclose its text), draw a ring
  const ring = RING_SPACE
  const pad =
    Math.max(px(style.paddingLeft), ring) +
    Math.max(px(style.paddingRight), ring)
  return {
    floor: Math.ceil(word + beside + pad),
    natural: Math.ceil(full + beside + pad),
  }
}

/**
 * A body or summary cell's natural width when its value truncates (an ellipsis cell or a clipped value):
 * the value's full text plus the cell's padding. 0 for a cell whose value does not truncate.
 * @param {HTMLElement} cell
 * @returns {number}
 */
export const truncatedNatural = cell => {
  const style = window.getComputedStyle(cell)
  const pad = px(style.paddingLeft) + px(style.paddingRight)
  let natural = 0
  if (style.textOverflow === 'ellipsis') natural = textWidths(cell).full
  cell.querySelectorAll(CLIPPED).forEach(el => {
    natural = Math.max(natural, textWidths(el).full)
  })
  return natural > 0 ? Math.ceil(natural + pad) : 0
}

// ---- a column's fit width: the narrowest it shows every rendered cell without truncating ----

const escapeKey = key =>
  typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(key)
    : String(key).replace(/["\\]/g, '\\$&')

// rows that span every column (row detail, a state surface, a skeleton) would widen the columns they span
const SPANNING =
  ':scope > tbody > tr.me-table-detail, :scope > tbody > tr.me-table-state, :scope > tbody > tr.me-table-skeleton-row, :scope > caption'

// Lays a table out at its content widths, inline so nothing outranks it: automatic layout at max-content
// width, no column widths, the measured column's cells free of the cap that truncates them, spanning rows
// out. Returns the undo.
const layOutAtContent = (table, cells) => {
  const saved = []
  const set = (el, prop, value) => {
    saved.push([el, prop, el.style.getPropertyValue(prop)])
    el.style.setProperty(prop, value, 'important')
  }
  set(table, 'table-layout', 'auto')
  set(table, 'width', 'max-content')
  set(table, 'min-width', '0px')
  table
    .querySelectorAll(':scope > colgroup > col')
    .forEach(col => set(col, 'width', 'auto'))
  table.querySelectorAll(SPANNING).forEach(row => set(row, 'display', 'none'))
  cells.forEach(cell => {
    set(cell, 'width', 'auto')
    set(cell, 'max-width', 'none')
  })
  return () =>
    saved.reverse().forEach(([el, prop, value]) => {
      if (value) el.style.setProperty(prop, value)
      else el.style.removeProperty(prop)
    })
}

/**
 * The width a column needs to show its header and every rendered body and summary cell without truncating:
 * each one's content on one line (a wrapping text's longest line unwrapped, a truncated value whole) plus its
 * cell's padding, the widest across every table of the unit (a grouped instance's sections). The tables are
 * laid out at their content widths, read and restored in one synchronous step, so they never paint so. Null
 * without layout (jsdom) or when the column renders no cell.
 * @param {HTMLElement} cell a cell of the column, such as the header cell holding its resize boundary
 * @param {string} key the column key
 * @returns {?number} CSS pixels, rounded up
 */
export const fitWidth = (cell, key) => {
  const unit = cell && cell.closest('.me-table-unit')
  const own = cell && cell.closest('table')
  const tables = unit
    ? Array.from(unit.querySelectorAll('.me-table-region > table.me-table'))
    : own
    ? [own]
    : []
  const selector = `:scope > * > tr > [data-column-key="${escapeKey(key)}"]`
  let width = 0
  tables.forEach(table => {
    const cells = Array.from(table.querySelectorAll(selector))
    if (!cells.length) return
    const undo = layOutAtContent(table, cells)
    try {
      width = Math.max(width, rectWidth(cells[0]))
    } finally {
      undo()
    }
  })
  return width > 0 ? Math.ceil(width) : null
}

const raise = (into, key, value) => {
  if (value > 0 && !(into[key] >= value)) into[key] = value
}

/**
 * Header floors and natural widths in a rendered table, keyed by column key.
 * @param {?HTMLTableElement} table
 * @returns {{floors: Object<string, number>, naturals: Object<string, number>}}
 */
export const measureHeaders = table => {
  const floors = {}
  const naturals = {}
  if (!table) return { floors, naturals }
  table.querySelectorAll(HEAD_CELLS).forEach(cell => {
    const { floor, natural } = headerExtent(cell)
    raise(floors, cell.dataset.columnKey, floor)
    raise(naturals, cell.dataset.columnKey, natural)
  })
  return { floors, naturals }
}

/**
 * The natural widths of truncating values in a rendered table, keyed by column key; null while the table
 * renders no data cells, so the caller keeps the widths it had.
 * @param {?HTMLTableElement} table
 * @returns {?Object<string, number>}
 */
export const measureTruncated = table => {
  if (!table) return null
  if (!table.querySelector(':scope > tbody > tr > td[data-column-key]'))
    return null
  const naturals = {}
  table.querySelectorAll(CELLS).forEach(cell => {
    raise(naturals, cell.dataset.columnKey, truncatedNatural(cell))
  })
  return naturals
}

const sameFloors = (a, b) => {
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => a[key] === b[key])
  )
}

const merge = (...maps) => {
  const out = {}
  maps.forEach(map =>
    Object.entries(map || {}).forEach(([key, value]) => raise(out, key, value))
  )
  return out
}

const NO_FLOORS = {}
const NOTHING = {
  floors: NO_FLOORS,
  naturals: NO_FLOORS,
  data: NO_FLOORS,
  truncated: NO_FLOORS,
}

/**
 * The content floors and natural widths of the unit's table, measured after a render whose rows, density,
 * columns or summary changed (`deps`), and again once web fonts finish loading. A column's floor is the
 * widest of its non-bending content and its header's longest word; its natural width is the widest of its
 * header's whole label and its truncating values. While no data cells render (loading, an empty state) the
 * data-cell measurements in force stay and only the header's are read again.
 * @param {{current: ?HTMLElement}} unitRef
 * @param {Array<*>} deps
 * @returns {{floors: Object<string, number>, naturals: Object<string, number>}}
 */
export const useContentFloors = (unitRef, deps) => {
  const [measured, setMeasured] = useState(NOTHING)

  useLayoutEffect(() => {
    const unit = unitRef.current
    const fonts = typeof document !== 'undefined' ? document.fonts : null
    let live = true
    const measure = () => {
      if (!live || !unitRef.current) return
      // every table of the unit: a grouped instance's sections share one set of columns, so a column's
      // floor is the widest any section needs
      const tables = unitRef.current.querySelectorAll(
        '.me-table-region > table.me-table'
      )
      let data = null
      let truncated = null
      const head = { floors: {}, naturals: {} }
      tables.forEach(table => {
        const found = measureFloors(table)
        if (found) data = merge(data, found)
        const clipped = measureTruncated(table)
        if (clipped) truncated = merge(truncated, clipped)
        const headers = measureHeaders(table)
        head.floors = merge(head.floors, headers.floors)
        head.naturals = merge(head.naturals, headers.naturals)
      })
      setMeasured(current => {
        const nextData = data || current.data
        const nextTruncated = truncated || current.truncated
        const floors = merge(nextData, head.floors)
        const naturals = merge(nextTruncated, head.naturals)
        if (
          sameFloors(current.floors, floors) &&
          sameFloors(current.naturals, naturals)
        )
          return current
        return { floors, naturals, data: nextData, truncated: nextTruncated }
      })
    }
    if (unit) measure()
    if (fonts && typeof fonts.addEventListener === 'function') {
      fonts.addEventListener('loadingdone', measure)
      if (fonts.ready && typeof fonts.ready.then === 'function')
        fonts.ready.then(measure)
    }
    return () => {
      live = false
      if (fonts && typeof fonts.removeEventListener === 'function')
        fonts.removeEventListener('loadingdone', measure)
    }
  }, deps)

  return measured
}
